import {
    Alignment, Api, CompanySettings, DisplayType, HorizontalAlignment, LicenseTester, Logger, LogManager, MixinUtil,
    ModelRow, Navigation, StringUtil, UrlUtil
} from "@mcleod/core";
import {
    Button, ChangeEvent, ClickEvent, Component, ContextMenuCreationEvent, DataSource, DataSourceRemoteDataChangeEvent,
    Image, KeyEvent, Label, Layout, Panel, SlideoutDecorator, Table, TableRow, TableRowDisplayEvent, Textbox
} from "@mcleod/components";
import { CommonTooltips } from "@mcleod/common";
import { setRowComponentVisible } from "@mcleod/powerbroker/src/BrokeragePlanningUtil";
import { AppointmentUtil, FilterPanel, SecondaryFilterProps, ShipmentStatusSlideout } from "../../index";
import { ManifestMovementQuickInfo } from "../../ManifestMovementQuickInfo";
import { RowOrderPlanningProfile } from "../../models/ModelOrderPlanningProfile";
import { StopUtil } from "../../StopUtil";
import { OrderPlanningLayout } from "../OrderPlanningLayout";
import { OrderPlanningSecondaryFilter } from "../OrderPlanningSecondaryFilter";
import { Tools, OrderPlanningToolPanel } from "../OrderPlanningToolPanel";
import { PlanningUtil } from "../PlanningUtil";
import { AutogenLayoutOrderPlanningOrders } from "./autogen/AutogenLayoutOrderPlanningOrders";
import { LinkedMoveSlideout, LinkedAction } from "../LinkedMoveSlideout";
import { LateRouteViewSlideout } from "../../asset/LateRouteViewSlideout";

const log = LogManager.getLogger("lme.dispatch.planning.opsmgmt.OrderPlanningOrders");

export class OrderPlanningOrders extends AutogenLayoutOrderPlanningOrders {
    private shipmentStatusError: boolean;
     private shipmentStatusCount: number;
    private availFilterPanel: FilterPanel;
    private availSecondaryFilters: SecondaryFilterProps[] = [
        OrderPlanningSecondaryFilter.FILTER_UNASSIGNED,
        OrderPlanningSecondaryFilter.FILTER_ASSIGNED,
        OrderPlanningSecondaryFilter.FILTER_APPT_REQUIRED,
        OrderPlanningSecondaryFilter.FILTER_APPT_CONFIRMED,
        OrderPlanningSecondaryFilter.FILTER_LATE,
        OrderPlanningSecondaryFilter.FILTER_SHIPMENT_STATUS_ERRORS,
        OrderPlanningSecondaryFilter.FILTER_ARRIVED_AT_SHIPPER
    ];

    getThisAsLayout(): Layout {
        return this;
    }

    getLog(): Logger {
        return log;
    }

    getTable(): Table {
        return this.tableAvailableMoves;
    }

    getToolsPanel(): OrderPlanningToolPanel {
        return this.layoutOPAvailTools.toolPanel;
    }

    override async onLoad() {
        this.setTitles();
        this.applyUrlParams();
        this.applyCommonTableSetup(this.tableAvailableMoves);
        const isEdiLicensed = LicenseTester.testPackage("com.tms.client.loadmaster.edi");
        const newFilters = isEdiLicensed ? this.availSecondaryFilters : this.availSecondaryFilters.filter(filter => filter.id !== OrderPlanningSecondaryFilter.FILTER_SHIPMENT_STATUS_ERRORS.id);
        this.availFilterPanel = new FilterPanel(newFilters, this.tableAvailableMoves, this.panelAvailFilters);
        await this.layoutOPAvailTools.showButtons(Tools.ORDERS_MANAGE_ASSIGN, Tools.DISPATCH, Tools.YSPLIT, Tools.HOLD,
            Tools.FINDNEAR, Tools.ZMIT, Tools.CANCEL_SENT_LOAD, Tools.SEND_MESSAGE, Tools.FEASIBILITY, Tools.UNASSIGN, Tools.LINK,
            Tools.UPDATE_FIELDS, Tools.TONU);
        PlanningUtil.showHideTrailerColumns(this.getTable())
        this.initTimer();
        if (this.isNested !== true)
            this.loadProfile();
        if (!CompanySettings.get().partial_shipment_enabled) {
            this.tableAvailableMoves.removeColumn("columnHeaderLength");
        }
    }

    private setTitles() {
        this.layoutPlanningTitle.majorTitle = "Orders";
        this.layoutPlanningTitle.minorTitle = "Planning";
    }

    doAfterProfileSet(value: RowOrderPlanningProfile) {
        PlanningUtil.showDateRange(this.textboxDateRange, value.get("start_day"), value.get("order_days"));
    }

    override async search(): Promise<any> {
        this.sourceAvailableMove.search({profile_row: this.profile})
            .then((response) => {
                // Add after search logic here
                this.setSecondaryFilters();
            }).catch(error => log.debug("AvailableMove search failed: %o", error));
    }

    private setSecondaryFilters() {
        if (this.shipmentStatusError) {
            this.availFilterPanel.toggleFilterById(OrderPlanningSecondaryFilter.FILTER_SHIPMENT_STATUS_ERRORS.id);
            this.shipmentStatusError = false;
        }
    }

    applyUrlParams() {
        const params = UrlUtil.getPropsFromUrl(window.location.href);
        this.shipmentStatusError = params?.["shipmentStatusError"];
        this.shipmentStatusCount = parseInt(params?.["planningOrdersCount"], 10) || 0;
    }

    tableAvailableMovesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow() as TableRow;
        const modelRow = tableRow.data as ModelRow;

        tableRow.contextMenuItems = () => this.getToolsPanel().getContextMenuItems(tableRow);
        tableRow.addContextMenuCreationListener((event: ContextMenuCreationEvent) => {
            this.getToolsPanel().contextMenuCreationEvent(event);
        });

        const manifestId = modelRow.get("manifest_id");
        const movementId = modelRow.get("movement_id");

        if (!StringUtil.isEmptyString(manifestId) && !StringUtil.isEmptyString(movementId)) {
            const manifestIdLabel = tableRow.findComponentById("labelManifestId") as Label;
            if (manifestIdLabel != null) {
                ManifestMovementQuickInfo.addHoverToDiscoverToManifestIdComponent(manifestIdLabel, movementId, manifestId);
            }
        }

        this.setupAvailableIcons(tableRow, modelRow);
        this.formatDrivers(tableRow, modelRow);
        this.setAvailTimeLabels(tableRow, modelRow);
        this.setStopsLabel(tableRow, modelRow);
        this.setLoadMatchFields(tableRow, modelRow);

        const labelETAToPickup = tableRow.findComponentById("labelEtaToPickup") as Label;
        if (labelETAToPickup != null) {
            labelETAToPickup.visible = !modelRow.isNull("origin_eta");
        }
        this.setArrivedBadge(tableRow, modelRow);

        const label = tableRow.findComponentById("labelOrderId") as Label;
        if (label != null) {
            label.visible = modelRow.isNull("manifest_id");
        }
        this.styleLinkLabel(label);
        const labelEmptyOrder = tableRow.findComponentById("labelEmptyOrder") as Label;
        labelEmptyOrder?.addUnderlineWhenMouseOver();
        const manifestIdLabel = tableRow.findComponentById("labelManifestId") as Label;
        if (manifestIdLabel != null)
            this.styleLinkLabel(manifestIdLabel);

        this.doEveryMinuteRow(tableRow);
        tableRow.forEveryChildComponent(comp => {
            PlanningUtil.setPlanningTooltipContent(comp, modelRow);
        })

        const textboxPlanningComment = tableRow.findComponentById("textboxPlanningComment") as Textbox;
        if (textboxPlanningComment != null) {
            const saveButton = tableRow.findComponentById("buttonSaveComment") as Button;
            textboxPlanningComment.addKeyDownListener(
                (event: KeyEvent) => PlanningUtil.savePlanningCommentOnKeyDown(event, saveButton)
            );
        }

        const labelOrderBol = tableRow.findComponentById("labelOrderBol") as Label;
        this.setupTruncatedDataTooltip(labelOrderBol);

        const labelConsigneeRefNumber = tableRow.findComponentById("labelConsigneeRefNumber") as Label;
        this.setupTruncatedDataTooltip(labelConsigneeRefNumber);
        this.setupOriginLateStatusPanel(tableRow);
        if (!modelRow.isNull("manifest_id")) {
            setRowComponentVisible(tableRow, "panelCommodity", "commodity", false);
            setRowComponentVisible(tableRow, "labelTrailerTypeDescr", "trailer_type_descr", false);
            setRowComponentVisible(tableRow, "labelCustomerName", "customer_name", false);
            setRowComponentVisible(tableRow, "textboxOperationsUser", "operations_user", false);
            setRowComponentVisible(tableRow, "textboxOrderType", "order_type", false);
            setRowComponentVisible(tableRow, "panelTemperature", "min_temp", false);
            setRowComponentVisible(tableRow, "textboxTotalCharge", "total_charge", false);
            setRowComponentVisible(tableRow, "textboxLength", "order_length", false);
        }
        else {
            setRowComponentVisible(tableRow, "textboxManifestOperationsUsers", "manifest_operations_users", false);
            setRowComponentVisible(tableRow, "textboxManifestOrderTypes", "manifest_order_types", false);
            setRowComponentVisible(tableRow, "textboxManifestTrailerTypes", "manifest_trailer_types", false);
            setRowComponentVisible(tableRow, "panelManifestCommodity", "manifest_commodities", false);
            setRowComponentVisible(tableRow, "textboxManifestCustomers", "manifest_customers", false);
            setRowComponentVisible(tableRow, "labelManifestTotalCharge", "manifest_total_charge", false);
            setRowComponentVisible(tableRow, "panelManifestTemperature", "manifest_min_temp", false);
        }

        PlanningUtil.determineOrderNumberVisibility(tableRow, modelRow);
    }

    handleRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        const source = event.target as DataSource;
        const changedRow = event.change.data as ModelRow;
        const index = source.findRowInData(changedRow);
        const currentRow = index >= 0 ? source.data[index] : null;

        this.testUpdateFreshness(event, currentRow);
        if (event.defaultPrevented !== true) {
            //this.syncOrderPlanningComments(currentRow, source);
            this.adjustEventType(event, currentRow);
        }
    }

    private initTimer() {
        const callback = () => {
            for (const tableRow of this.tableAvailableMoves.rows) {
                if (!tableRow.populatedDOM)
                    return;
                this.doEveryMinuteRow(tableRow);
            }
        };
        this.startMinuteTimer(callback);
    }

    private doEveryMinuteRow(tableRow: TableRow) {
        // call your available update function(s) here
        PlanningUtil.checkScheduledLate(tableRow, "origin");
        PlanningUtil.checkScheduledLate(tableRow, "destination");
    }

    private setupAvailableIcons(tableRow: TableRow, modelRow: ModelRow) {
        this.displayHoldIcon(tableRow);
        PlanningUtil.setupYsplitIcons(tableRow, modelRow);
        this.setBrokerageIcon(modelRow, tableRow);
        this.displayPackageErrorImage(tableRow);
    }

    private setBrokerageIcon(modelRow: ModelRow, tableRow: TableRow) {
        const image = tableRow.findComponentById("imageBrokerage") as Image;
        image.visible = modelRow.getBoolean("brokerage", false);
    }

    private displayHoldIcon(tableRow: TableRow) {
        const modelRow = tableRow.data as ModelRow;
        const holdBadge = tableRow.findComponentById("imageHold") as Label;
        const manifestOnHold: boolean = modelRow.getBoolean("manifest_hold", false);
        const manifestId = modelRow.get("manifest_id");
        if (StringUtil.isEmptyString(manifestId) )
            CommonTooltips.setTooltipContentTextboxes(holdBadge, () => [{
                caption: "Reason",
                text: tableRow.data.get("hold_reason")
            }]);
        holdBadge.visible = false;
        if (StringUtil.isEmptyString(manifestId) && modelRow.getBoolean("hold", false) === true) {
            holdBadge.visible = true;
        }
        else if(!StringUtil.isEmptyString(manifestId) && manifestOnHold === true) {
            holdBadge.visible = true;
            holdBadge.tooltip = "Manifest on hold"
        }
        if (holdBadge.visible)
            holdBadge.text = "HOLD";
    }

    private formatDrivers(tableRow: TableRow, modelRow: ModelRow) {
        const labelDriver = tableRow.findComponentById("labelDriver1Name") as Label;
        const driver1 = !modelRow.isNull("driver1_name");
        const driver2 = !modelRow.isNull("driver2_name");
        if (labelDriver && driver1 && driver2) {
            labelDriver.text = `${modelRow.get("driver1_name")},`;
        }
    }

    private setAvailTimeLabels(tableRow: TableRow, modelRow: ModelRow) {
        this.displayArriveLateTime(tableRow, "labelOriginLateTime", "origin_late", modelRow.get("same_day_pickup_window"), "panelOriginLate");
        this.displayArriveLateTime(tableRow, "labelDestLateTime", "destination_late", modelRow.get("same_day_delivery_window"), "panelDestLate");

        AppointmentUtil.setAppointmentImageForTableRow(tableRow, "imageOrigApptReq", "origin_appt_required", "origin_appt_confirmed");
        AppointmentUtil.setAppointmentImageForTableRow(tableRow, "imageDestApptReq", "destination_appt_required", "destination_appt_confirmed");
    }

    private setArrivedBadge(tableRow: TableRow, modelRow: ModelRow) {
        const arrived = !modelRow.isNull("origin_actual_arrival")
            && modelRow.isNull("origin_actual_departure");

        const arrivedBadge = tableRow.findComponentById("labelArrived") as Label;
        arrivedBadge.visible = arrived;

        const distancePickup = tableRow.findComponentById("labelDistanceToPickup") as Label;
        distancePickup.visible = !arrived;
    }

    private displayPackageErrorImage(tableRow: TableRow) {
        const imagePackageError = tableRow.findComponentById("imagePackageError") as Image;
        if (tableRow.data.getBoolean("has_shipment_error")) {
            imagePackageError.visible = true;
            CommonTooltips.setTooltipContentLayout(imagePackageError, null, "lme/datafusion/StatusErrorQuickInfo", null,
                {"parent_id": tableRow.data.get("status_id"), "parent_tablename": "edistatus"})
            imagePackageError.tooltipDynamicOptions = { position: Alignment.BOTTOM };
        } else {
            imagePackageError.visible = false;
        }
    }

    private setupTruncatedDataTooltip(label: Label) {
        label.tooltipDynamicContent = () => {
            // Only set a tooltip if the text would be truncated in the UI
            const dataValue = label.getDataValue();
            if (dataValue && (label._element.scrollWidth + 10 > Number(label.parent.width))) {
                return `${dataValue}`;
            }
            return null;
        };
    }

    private setupOriginLateStatusPanel(tableRow: TableRow, ) {
        const labelOriginLateStatus = tableRow?.findComponentById("labelOriginLateStatus") as Label;
        const originLateStatus = tableRow?.data?.get("origin_late_status");
        const panel = tableRow?.findComponentById("panelOriginLateStatus") as Panel;
        const bgColor = tableRow.data.get("late_route_status_color");
        StopUtil.setupOriginLateStatusPanel(panel, labelOriginLateStatus, originLateStatus, bgColor);
    }

    private setStopsLabel(tableRow: TableRow, modelRow: ModelRow) {
        const pickDropLabel = tableRow.findComponentById("labelStopsPickDrop") as Label;
        const text = modelRow.get("stops_pick_drop");
        if (!text) return;
        pickDropLabel.text = text;
        PlanningUtil.setLinkStyles(pickDropLabel);
        pickDropLabel.addUnderlineWhenMouseOver();

        AppointmentUtil.setIntermediateAppointmentImageAndLabel({
            tableRow: tableRow,
            imageRequiredId: "imageApptRequiredCount",
            labelRequiredId: "labelApptRequiredCount",
            requiredField: "appt_required_count",
            imageConfirmedId: "imageApptConfirmedCount",
            labelConfirmedId: "labelApptConfirmedCount",
            confirmedField: "appt_confirmed_count",
        })
    }

    private handleAppointmentOnClick(event: ClickEvent) {
        const image = event.target as Image;
        PlanningUtil.showMultiStopSlideout(image, true)
    }

    private setLoadMatchFields(tableRow: TableRow, modelRow: ModelRow) {
        const labelRevenuePerDist = tableRow.findComponentById("labelRevenueDistance") as Label;
        const revenuePerDist = modelRow.getCurrency("revenue_distance", null)?.getAmount();
        if ( revenuePerDist && revenuePerDist < 0) {
            labelRevenuePerDist.displayType = DisplayType.STRING;
            labelRevenuePerDist.align = HorizontalAlignment.RIGHT;
            labelRevenuePerDist.text = "Error";
        }
    }

    openStatusSlideout(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        const id = row.data.get("order_id");
        const moveId = row.data.get("movement_id");
        const customerId = row.data.get("customer_id");
        if (id) {
            const layout = Layout.getLayout("lme/dispatch/ShipmentStatusSlideout", {
                width: window.innerWidth * .70,
                backgroundColor: "defaultBackground",
                fillHeight: true,
                paddingBottom: 50
            }) as ShipmentStatusSlideout;
            new SlideoutDecorator({
                layout: layout,
                title: "DataFusion - Order " + id,
                fillVerticalSpace: true,
                addlComponents: [layout.saveButton],
                doAfterSlideIn: (decorator: SlideoutDecorator) => {
                    layout.sourceEdistatus.search({order_id: id});
                    layout.sourceEdiOrder.search({order_id: id, movement_id: moveId});
                    layout.sourceEdiMovementCarrier.search({movement_id: moveId});
                    layout.tabsetDataFusion.selectedIndex = layout.tabsetDataFusion.indexOf(layout.tabOutbound);
                    layout.saveButton.addClickListener(event => {
                        if (decorator.validateSimple()) {
                            const modelRows = layout.outboundDataSource.data;
                            modelRows?.forEach(async (data, index) => {
                                if (data.hasChanged()) {
                                    await layout.outboundDataSource.data[index].post();
                                    layout.sourceEdistatus.search({order_id: id});
                                }
                            });
                        }
                    });
                },
                doAfterSlideOut: (decorator: SlideoutDecorator) => this.search()
            });
            layout.orderId = id;
            layout.movementId = moveId;
            layout.customerId = customerId;
        }
    }

    multistopOnClick(event: ClickEvent) {
        PlanningUtil.showMultiStopSlideout(event.target as Label, true);
    }

    private buttonEditCommentOnClick(event: ClickEvent) {
        PlanningUtil.buttonEditCommentOnClickListener(event);
    }

    private async buttonSaveCommentOnClick(event: ClickEvent) {
        PlanningUtil.buttonSaveCommentOnClickListener(event);
    }

    private buttonCancelCommentOnClick(event: ClickEvent) {
        PlanningUtil.buttonCancelCommentOnClickListener(event);
    }

    private textboxPlanningCommentOnClick(event: ClickEvent) {
        PlanningUtil.textboxPlanningCommentOnClickListener(event);
    }

    private textboxPlanningCommentOnDblClick(event: ClickEvent) {
        PlanningUtil.textboxPlanningCommentOnDblClickListener(event);
    }

    private textboxPlanningCommentOnChange(event: ChangeEvent) {
        PlanningUtil.textboxPlanningCommentOnChangeListener(event);
    }

    /** This is an event handler for the onClick event of labelOriginLateStatus. */
    async labelOriginLateStatusOnClick(event: ClickEvent) {
        const tableRow = TableRow.getContainingTableRow(event.target as Component);
        const modelRow = tableRow.data as ModelRow;
        const response = await Api.search("lme/dispatch/planning/late-route", {
            "movement_id": event.target["row"]?.data.get("movement_id"),
            "is_current": "Y"
        });
        new LateRouteViewSlideout({
            movementId: modelRow.get("movement_id"),
            stopId: modelRow.get("origin_stop_id"),
            isCurrent: response.actual_row_count > 0
        });

    }

    async labelOriginLateStatusChevronOnClick(event: ClickEvent) {
        try {
            const tableRow = TableRow.getContainingTableRow(event.target as Component);
            const modelRow = tableRow.data as ModelRow;
            const panel = tableRow.findComponentById("panelOriginLateStatus") as Panel;
            const stopId = modelRow.get("origin_stop_id");
            const movementId = modelRow.get("movement_id");
            await PlanningUtil.labelNextStopLateStatusChevronOnClick(event, panel, stopId, movementId);
        } catch (error) {
            log.error("Error in labelNextStopLateStatusChevronOnClick:", error);
        }
    }

    manifestIdOnClick(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        Navigation.navigateTo("lme/dispatch/OrderList?manifest_id=" + row.data.get("manifest_id"), { newTab: true });
    }

    imageLinkedOnClick(event: ClickEvent) {
        const row: TableRow = TableRow.getContainingTableRow(event.target as Component);
        new LinkedMoveSlideout({ linkedId: row.data.get("linked_id"), linkedAction: LinkedAction.EDIT });
    }
}
MixinUtil.applyMixin(OrderPlanningOrders, OrderPlanningLayout);

// TypeScript interface declaration for mixin integration
export interface OrderPlanningOrders extends OrderPlanningLayout {
}
